import type { Battle } from "@loot-os/api-contract";
import { capitalizeTerm } from "@loot-os/api-contract";
import { Badge } from "@loot-os/ui/components/badge";
import { Button } from "@loot-os/ui/components/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@loot-os/ui/components/card";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { seedQueryOptions } from "../../lib/queries.js";
import { useTerminology } from "../../lib/terminology.js";

/**
 * The battle's fairness inputs: its salt, the daily seed's commitment (and the seed once its day
 * has passed), and every revealed open's client seed, nonce and result, so any of them can be
 * recomputed. Nonces are battle-local: `round × seats + seat`.
 */
export function BattleFairnessPanel({ battle }: { battle: Battle }) {
  const [open, setOpen] = useState(false);
  const { battle: term } = useTerminology();
  const seed = useQuery({
    ...seedQueryOptions(battle.seedDate ?? "none"),
    enabled: open && !!battle.seedDate,
  });
  return (
    <Card>
      <CardHeader className="flex flex-row items-start justify-between gap-2">
        <div>
          <CardTitle className="text-sm">Provably fair</CardTitle>
          <CardDescription>
            Every pull is HMAC-SHA512(server seed, client seed + {term.singular} salt + nonce). The
            server seed is revealed after its UTC day ends.
          </CardDescription>
        </div>
        <Button size="sm" variant="secondary" onClick={() => setOpen((v) => !v)}>
          {open ? "Hide" : "Show"}
        </Button>
      </CardHeader>
      {open ? (
        <CardContent className="grid gap-3 text-sm">
          <dl className="grid gap-1.5 sm:grid-cols-2">
            <Fact label={`${capitalizeTerm(term.singular)} salt`} value={battle.salt} mono />
            <Fact label="Seed date (UTC)" value={battle.seedDate ?? "not started"} mono />
            <Fact label="Server seed hash" value={seed.data?.seedHash ?? "…"} mono />
            <Fact
              label="Server seed"
              value={seed.data ? (seed.data.seed ?? "revealed after the UTC day ends") : "…"}
              mono
            />
          </dl>
          {battle.results.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b border-border text-left text-muted-foreground">
                    <th className="py-1 pr-3">Round</th>
                    <th className="py-1 pr-3">Seat</th>
                    <th className="py-1 pr-3">Client seed</th>
                    <th className="py-1 pr-3">Nonce</th>
                    <th className="py-1 pr-3">Result</th>
                    <th className="py-1">Item</th>
                  </tr>
                </thead>
                <tbody>
                  {battle.results.flatMap((round) =>
                    round.map((open) => (
                      <tr
                        key={`${open.roundIndex}-${open.seatIndex}`}
                        className="border-b border-border/60"
                      >
                        <td className="py-1 pr-3">{open.roundIndex + 1}</td>
                        <td className="py-1 pr-3">{open.seatIndex + 1}</td>
                        <td className="py-1 pr-3 font-mono">
                          {battle.seats[open.seatIndex]?.clientSeed ?? "—"}
                        </td>
                        <td className="py-1 pr-3 font-mono">{open.nonce}</td>
                        <td className="py-1 pr-3 font-mono">{open.result}</td>
                        <td className="py-1">
                          {open.item.name}{" "}
                          <span className="text-muted-foreground">(index {open.itemIndex})</span>
                        </td>
                      </tr>
                    )),
                  )}
                </tbody>
              </table>
            </div>
          ) : (
            <Badge variant="outline">No rounds revealed yet</Badge>
          )}
          {battle.status === "completed" ? (
            <a
              className="text-xs underline"
              href={`/api/loot/v1/battles/${battle.id}/verification`}
              target="_blank"
              rel="noreferrer"
            >
              Raw verification data (JSON)
            </a>
          ) : null}
        </CardContent>
      ) : null}
    </Card>
  );
}

function Fact({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="grid gap-0.5">
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd className={`break-all ${mono ? "font-mono text-xs" : ""}`}>{value}</dd>
    </div>
  );
}
